import retry from 'async-retry';
import Axios from 'axios';
import axios, { AxiosError } from 'axios';
import { signIn } from 'next-auth/react';

import User from '../../assets/icons/User';
import { ConsumerGenerator } from '../../components/dynamicPages/CreatePage/TestPrepTab';
import {
  CuripodDecendant,
  CuripodElement,
} from '../../components/SlideComponents/slateUtil';
import { Slide } from '../../components/SlideComponents/slide.types';
import { AiAssessedScore } from '../../frontendServices/gameAPI';
import {
  OrganizationType,
  RawUserAccessBadge,
  UserAccessBadge,
} from '../../hooks/useAccessBadge';
import { newRelicNoticeError } from '../../hooks/useNewRelic';
import { ReportInsight, ReportInsightType } from '../../hooks/useReportInsights';
import { TenorGIF } from '../../hooks/useTenorGIFs';
import pkg from '../../package.json';
import { preventRedirectOn401Pahtnames } from '../../utils/backend-config';
import { notUndefined } from '../../utils/general';
import { getDestinationTenantFromUrl } from '../../utils/navigator';
import { NEW_TEMPLATE_PARAM } from '../../utils/urlUtils';
import Metrics from '../metrics/metrics';
import {
  Folder,
  FolderEditOrCreateDTO,
  GeneratorCoarseLicenseLevel,
  TemplateEditOrCreateDTO,
  Workspace,
} from '../TemplateService/Template.type';
import { Template } from '../TemplateService/Template.type';
import {
  ActivityResults,
  ById,
  CropConfig,
  DiscoveredTemplate,
  DiscoveredTemplateStatus,
  DistrictUsageData,
  EmailInvitationCreateDTO,
  FileUploads,
  FileUploadType,
  Game,
  GameEditOrCreateDTO,
  Invitation,
  InvitationRole,
  LeaderBoard,
  LessonContext,
  MSClaims,
  PaginatedGameResults,
  PreferredMimeType,
  Presentation,
  PresentationDTO,
  ProcessDocumentErrorMessage,
  PromptInputData,
  PromptResourceMap,
  PromptSingleTestCaseResult,
  PromptTestcase,
  Response,
  RoundOptions,
  RoundType,
  SchoolUsageData,
  Score,
  Standards,
  SupportedIntegrationLabel,
  SupportedIntegrationResponse,
  UserTenant,
} from './types';

const getAuthHeader = (token: string, destinationTenantId?: string) => {
  const base: ById<string> = {
    Authorization: `Bearer ${token}`,
    'x-app-version': `v${pkg.version}`,
  };
  if (destinationTenantId) {
    base['x-api-tenant'] = destinationTenantId;
  }
  return base;
};
const getUnAuthHeader = (destinationTenantId?: string) => {
  const base: ById<string> = {
    'x-app-version': `v${pkg.version}`,
  };
  if (destinationTenantId) {
    base['x-api-tenant'] = destinationTenantId;
  }
  return base;
};

export enum ConfirmationState {
  NeverConfirmAgain = 'NeverConfirmAgain',
  ConfirmedForDuration = 'ConfirmedForDuration',
  NeedsConfirmation = 'NeedsConfirmation',
}

export default class BackendService {
  static instance: BackendService;
  url = '';
  idToken: string | null = null;
  cdnUrl = '';
  deferredRefreshRequests: { resolve: () => void; reject: () => void }[] = [];
  isRefreshingTokens = false;
  constructor() {
    if (BackendService.instance) {
      return BackendService.instance;
    }
    BackendService.instance = this;
    return this;
  }

  public init(url: string, cdnURL: string) {
    BackendService.instance.url = url;
    // BackendService.instance.auth = auth;
    BackendService.instance.cdnUrl = cdnURL;

    if (process.env.isLocal) {
      BackendService.instance.url = 'http://localhost:3010';
    }
  }

  public setToken = (token: string) => {
    this.idToken = token;
  };

  private clearToken = () => {
    this.idToken = null;
  };

  private getToken() {
    const token = this.idToken;
    if (!token) {
      throw new Error('Token does not exists');
    }
    return token;
  }
  private getRefreshToken = async () => {
    if (!this.isRefreshingTokens) {
      // We will fetch a new token

      // set to true to make sure this is the only one actually fetching
      this.isRefreshingTokens = true;
      try {
        const res = await axios.get<{ id_token: string }>(
          `${window.location.origin}/api/auth/refreshToken`,
        );
        const token = res.data.id_token;

        // Set the fresh token in memory
        this.setToken(token);

        // resolve all deferred requests
        this.deferredRefreshRequests.forEach(cb => cb.resolve());

        // reset
        this.deferredRefreshRequests = [];
        this.isRefreshingTokens = false;
      } catch (e) {
        // Token could not be refreshed

        // remove the token in memory
        this.clearToken();

        // reject all deferred requests
        this.deferredRefreshRequests.forEach(cb => cb.reject());
        newRelicNoticeError(e, 'Error while refreshing token');

        if (this.redirectOnFail(window.location.pathname)) {
          // redirect the user back to where you are currently
          await signIn('azure-ad-b2c', { callbackUrl: window.location.href });
        }
      }
    } else {
      // There is a pending request - wait for it
      return new Promise<void>((resolve, reject) => {
        // Push this promise to the deferredRequests array to that they can be
        // resolved or rejected by the singular refresh token fetcher
        this.deferredRefreshRequests.push({
          resolve,
          reject,
        });
      });
    }
  };
  private redirectOnFail(pathname: string) {
    if (pathname === '/') return false;
    if (pathname.endsWith('/live/results')) return false;
    if (preventRedirectOn401Pahtnames.includes(pathname)) return false;

    // checking if the pathname is /:tenantId/lessons/:lessonId
    const pathnameSplitBySlash = window.location.pathname.split('/');
    if (pathnameSplitBySlash.length === 4 && pathnameSplitBySlash[2] === 'lessons') {
      return false;
    }
    return true;
  }

  public getSignOutUrl = async (): Promise<string> => {
    return retry(async () => {
      const res = await axios.post<{ url: string }>(
        `${window.location.origin}/api/auth/signoutUrl`,
      );
      return res.data.url;
    });
  };

  private async retryWithFreshTokenIf401<T>(cb: (token: string) => Promise<T>) {
    try {
      const token = this.getToken();
      return await cb(token);
    } catch (error) {
      if ((error as AxiosError).response?.status === 401) {
        // In case of 401, the token might be invalidated by the server, so we need a fresh one and retry
        // This might attempt a redirect
        await this.getRefreshToken();
        return await cb(this.getToken());
      }
      if ((error as AxiosError).response?.status === 500) {
        newRelicNoticeError(
          error,
          'Error while fetching data from backend, retryWithFreshTokenIf401',
        );
      }
      throw error as AxiosError;
    }
  }

  public getWebImage = async (url?: string) => {
    if (!url) return Promise.resolve(undefined);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = await axios.get<any>(url);
    return res;
  };

  public async joinTenant(invitationCode: string) {
    return this.retryWithFreshTokenIf401(async token => {
      const res = await axios.post<{
        roles: MSClaims;
        tenantId: string;
      }>(
        `${this.url}/v0.5/auth/selfServeJoin`,
        { invitationCode },
        {
          headers: getAuthHeader(token),
        },
      );
      return res.data;
    });
  }

  public async retrieveCurrentTenants() {
    return this.retryWithFreshTokenIf401(async token => {
      const res = await axios.get<{
        user: UserTenant;
      }>(`${this.url}/v0.5/tenants/myTenants`, {
        // Pass on the x-api-header to the api if we think we know the destination tenant
        headers: getAuthHeader(token),
      });
      return res.data;
    });
  }

  public async switchOrganization(organizationId: string) {
    return this.retryWithFreshTokenIf401(async token => {
      const res = await axios.post<UserTenant>(
        `${this.url}/v0.5/users/me/switchOrganization`,
        { organizationId },
        {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    });
  }
  public async getUploadUrl(pathToFile: string) {
    const tenants = await this.retrieveCurrentTenants();
    const activeTenantId = tenants.user.activeTenantId;
    return this.retryWithFreshTokenIf401(async token => {
      return await axios.post<string>(
        `${this.url}/v0.5/files/uploadUrl`,
        { pathToFile: pathToFile },
        {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
        },
      );
    });
  }

  public async getUploadUrlV2({
    pathToFile,
    fileType,
    mimeType,
    tenantId,
    fileName,
  }: {
    pathToFile: string;
    fileType: FileUploadType;
    mimeType: string;
    tenantId: string;
    fileName?: string;
  }) {
    return this.retryWithFreshTokenIf401(async token => {
      return await axios.post<{ url: string; uploadId: string }>(
        `${this.url}/v0.5/files/uploadUrlV2`,
        { pathToFile, fileType, mimeType, fileName },
        {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
        },
      );
    });
  }

  public async markUploadSuccessfulV2(uploadId: string, tenantId: string) {
    return this.retryWithFreshTokenIf401(async token => {
      const response = await axios.post<{ fileUpload: FileUploads }>(
        `${this.url}/v0.5/files/markUploadSuccessV2`,
        { uploadId: uploadId },
        {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
        },
      );

      return response.data;
    });
  }

  public async getDownloadUrl(filename: string, folder?: string) {
    return this.retryWithFreshTokenIf401(async token => {
      return await axios.post<{ url: string }>(
        `${this.url}/v0.5/files/downloadUrl`,
        { filename: filename, folder },
        {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        },
      );
    });
  }
  public async getPublicDownloadUrl(filename: string, folder?: string) {
    return await axios.post<{ url: string }>(
      `${this.url}/v0.5/files/public/downloadUrl`,
      { filename: filename, folder },
      {
        headers: getUnAuthHeader(getDestinationTenantFromUrl()),
      },
    );
  }

  public async getUploadedDesignerImages() {
    return this.retryWithFreshTokenIf401(async token => {
      const res = await axios.get<FileUploads[]>(
        `${this.url}/v0.5/files/uploadedDesignerImage`,
        {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    });
  }
  materials = {
    processDocument: async ({
      fileUpload,
      classifyContext,
    }: {
      fileUpload: FileUploads;
      classifyContext?: LessonContext;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          fileUpload: FileUploads;
          classification?: 'embed' | 'inform';
        }>(
          `${this.url}/v0.5/files/processDocument`,
          {
            fileUploadId: fileUpload.id,
            tenantId: fileUpload.tenantId,
            fileId: fileUpload.fileId,
            mimeType: fileUpload.mimeType,
            classifyContext,
          },
          { headers: getAuthHeader(token, getDestinationTenantFromUrl()) },
        );
        return res.data;
      }).catch(
        (
          error: AxiosError<{ error: { message: ProcessDocumentErrorMessage } }> | Error,
        ) => {
          // i don't like how this is running on the honor system;
          // surely there's a better way to do this with axios or maybe trpc
          // that we can figure out later so we have a better, easier to use pattern
          if ('isAxiosError' in error && error.isAxiosError) {
            const message = error.response?.data?.error?.message;
            switch (message) {
              case 'Failed to find content in parsed file':
                throw new Error('Failed to find content in parsed file');
              case 'Failed to process document':
                throw new Error('Failed to process document');
              case 'Unknown failure reason':
              default:
                throw new Error('Unknown failure reason');
            }
          }
          throw error;
        },
      );
    },
  };

  generatorPlugin = {
    generateLearningStandard: async ({
      prompt,
    }: {
      prompt: {
        searchValue: string | undefined;
        standardSelected: string | undefined;
        subjectSelected: string | undefined;
        gradeSelected: string | undefined;
      };
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const queryParams = new URLSearchParams();
        queryParams.set('prompt', prompt.searchValue || '');
        queryParams.set('state', prompt.standardSelected || '');
        queryParams.set('subject', prompt.subjectSelected || '');
        queryParams.set('grade', prompt.gradeSelected || '');
        const query = queryParams.toString();
        const res = await axios.get<PromptResourceMap>(
          `${this.url}/v0.5/standards/generate${query ? `?${query}` : ''}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );

        return res.data;
      });
    },
    generateExampleAnswers: async ({
      prompt,
      userInputFormData,
      resourceMap,
      categories,
    }: {
      prompt: string;
      userInputFormData: { id: string; type: string; value: string; fileType?: string }[];
      resourceMap: PromptResourceMap;
      categories: { id: string; prompt: string }[];
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<PromptResourceMap>(
          `${this.url}/v0.5/generators/generate-student-answers`,
          {
            prompt,
            resourceMap,
            userInputFormData,
            categories,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    generateSinglePromptTestCase: async ({
      presentationId,
      promptId,
      userInputFormData,
      resourceMap,
    }: {
      presentationId: string;
      promptId: string;
      userInputFormData: { id: string; type: string; value: string; fileType?: string }[];
      resourceMap: PromptResourceMap;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<PromptSingleTestCaseResult>(
          `${this.url}/v0.5/generators/generate-by-prompt`,
          {
            presentationId,
            promptId,
            resourceMap,
            userInputFormData,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    generateSingleSlidePromptTestCase: async ({
      presentationId,
      slideId,
      userInputFormData,
      resourceMap,
    }: {
      presentationId: string;
      slideId: string;
      userInputFormData: { id: string; type: string; value: string; fileType?: string }[];
      resourceMap: PromptResourceMap;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<PromptSingleTestCaseResult>(
          `${this.url}/v0.5/generators/generate-by-slide-prompt`,
          {
            presentationId,
            slideId,
            resourceMap,
            userInputFormData,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    generateSingleSlideMulitResponsePromptTestCase: async ({
      presentationId,
      slideId,
      userInputFormData,
      resourceMap,
    }: {
      presentationId: string;
      slideId: string;
      userInputFormData: { id: string; type: string; value: string; fileType?: string }[];
      resourceMap: PromptResourceMap;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<PromptResourceMap>(
          `${this.url}/v0.5/generators/generate-by-slide-multi-response-prompt`,
          {
            presentationId,
            slideId,
            resourceMap,
            userInputFormData,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );

        return res.data;
      });
    },

    getGeneratorThumbnailUrl: (fileId: string) => `${this.cdnUrl}/${fileId}`,

    getGeneratorCoverImageUploadUrlV2: async ({
      imageId,
      imageType,
      mimeType,
    }: {
      imageId: string;
      imageType: string;
      mimeType: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        return await axios.post<{ url: string; uploadId: string }>(
          `${this.url}/v0.5/files/global/uploadUrlV2`,
          { pathToFile: `generators/${imageId}`, fileType: imageType, mimeType },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
      });
    },
  };
  versionControl = {
    listPresentationVersions: async ({
      presentationId,
      cursor,
      limit,
    }: {
      presentationId: string;
      cursor?: string;
      limit?: number;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const queryParams = new URLSearchParams();
        if (cursor) queryParams.set('cursor', cursor);
        if (limit) queryParams.set('limit', limit.toString());
        const query = queryParams.toString();
        const res = await axios.get<Presentation[]>(
          `${this.url}/v0.5/presentations/${presentationId}/versions${
            query ? `?${query}` : ''
          }`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    restorePresentationVersion: async ({
      presentationId,
      versionId,
    }: {
      presentationId: string;
      versionId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Presentation>(
          `${this.url}/v0.5/presentations/${presentationId}/versions/${versionId}/restore`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    restoreLessonSnapshot: async ({
      presentationId,
      versionId,
    }: {
      presentationId: string;
      versionId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{ slides: Slide[]; currentSlideId?: string }>(
          `${this.url}/v0.5/presentations/${presentationId}/versions/${versionId}/restore`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };
  ai = {
    consumeGeneratorAsNewPresentation: async (
      {
        generatorId,
        userInputFormData,
      }: {
        generatorId: string;
        userInputFormData: PromptInputData[];
      },
      activeTenantId: string,
    ) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios
          .post<Template>(
            `${this.url}/v0.5/generators/generate-presentation`,
            { presentationId: generatorId, userInputFormData },
            {
              headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
              timeout: 1000 * 240,
            },
          )
          .catch(error => {
            const handleError = (
              event: string,
              newRelicMessage: string,
              customMessage: string,
            ) => {
              error.customMessage = customMessage;
              Metrics.getLogger().logEvent(event, {
                generatorId,
                userInputFormData,
                activeTenantId,
              });
              newRelicNoticeError(newRelicMessage, {
                error,
                generatorId,
                userInputFormData,
                activeTenantId,
              });
              throw error;
            };

            if (error.code === 'ECONNABORTED') {
              return handleError(
                'AI.ConsumeGeneratorAsNewPresentation.Timeout',
                'Timeout error creating lesson',
                `It's taking a bit longer than expected to create your lesson. It might still show up. Check My Lessons in a few minutes, or please try again.`,
              );
            }

            if (error.isAxiosError && error.message.toLowerCase() === 'network error') {
              return handleError(
                'AI.ConsumeGeneratorAsNewPresentation.ConnectionError',
                'Connection error creating lesson',
                `Something didn't go as planned. Please try again, or reach out in the chat for help!`,
              );
            }

            if (error.response?.status === 500) {
              return handleError(
                'AI.ConsumeGeneratorAsNewPresentation.ServerError',
                'Server error creating lesson',
                `There was a technical issue on our side. Please try your prompt again later. Our team is working to fix this!`,
              );
            }

            if (error.response?.status === 503) {
              return handleError(
                'AI.ConsumeGeneratorAsNewPresentation.ServiceUnavailable',
                'Service unavailable error creating customizable lesson',
                `There was a technical issue on our side. Please try your prompt again later. Our team is working to fix this!`,
              );
            }

            if (error.response?.status === 429) {
              return handleError(
                'AI.ConsumeGeneratorAsNewPresentation.TooManyRequests',
                'Too many requests creating customizable lesson',
                `It looks like too many teachers are generating lessons right now! We appreciate your patience. Please wait a moment and try again.`,
              );
            }

            return handleError(
              'AI.ConsumeGeneratorAsNewPresentation.ConnectionError',
              'Unknown error creating customizable lesson',
              `Something didn't go as planned. Please try again, or reach out in the chat for help!`,
            );
          });
        return res.data;
      });
    },
    /**
     * @deprecated Use createFullLesson instead. This method will be removed in a future version.
     */
    createDynamicLesson: async (
      {
        prompt,
        grade,
        subject,
        fileType,
        filePath,
        languages,
        lessonCategory, //This is the lesson intent - concider renaming eventually
      }: {
        prompt: string;
        grade: string;
        subject: string | null;
        fileType: string | null;
        filePath: string | null;
        languages: string[];
        lessonCategory: string | null;
      },
      activeTenantId: string,
    ) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios
          .post<Template>(
            `${this.url}/v0.5/generators/generate-dynamic-lesson`,
            {
              prompt,
              grade,
              subject,
              fileType,
              filePath,
              languages,
              lessonCategory,
            },
            {
              headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
              timeout: 1000 * 240,
              timeoutErrorMessage: 'Request timed out after 4 minutes',
            },
          )
          .catch(error => {
            const handleError = (
              event: string,
              newRelicMessage: string,
              customMessage: string,
            ) => {
              error.customMessage = customMessage;
              Metrics.getLogger().logEvent(event, {
                prompt,
                grade,
                subject,
                fileType,
                filePath,
                languages,
                lessonCategory,
                tenantId: activeTenantId,
              });
              newRelicNoticeError(newRelicMessage, {
                error,
                prompt,
                grade,
                subject,
                fileType,
                filePath,
                languages,
                lessonCategory,
                activeTenantId,
              });
              throw error;
            };

            if (error.code === 'ECONNABORTED') {
              return handleError(
                'AI.DynamicLesson.Timeout',
                'Timeout error creating dynamic lesson',
                `It's taking a bit longer than expected to create your lesson. It might still show up. Check My Lessons in a few minutes, or please try again.`,
              );
            }

            if (error.isAxiosError && error.message.toLowerCase() === 'network error') {
              return handleError(
                'AI.DynamicLesson.NetworkError',
                'Network error creating dynamic lesson',
                `It looks like there's a network connection issue. Please check your internet connection and try again. If this problem persists, it could be due to a poor connection or firewall settings.`,
              );
            }

            if (error.response?.status === 500) {
              return handleError(
                'AI.DynamicLesson.ServerError',
                'Server error creating dynamic lesson',
                `There was a technical issue on our side. Please try your prompt again later. Our team is working to fix this!`,
              );
            }

            if (error.response?.status === 503) {
              return handleError(
                'AI.DynamicLesson.ServiceUnavailable',
                'Service unavailable error creating dynamic lesson',
                `There was a technical issue on our side. Please try your prompt again later. Our team is working to fix this!`,
              );
            }

            if (error.response?.status === 429) {
              return handleError(
                'AI.DynamicLesson.RateLimit',
                'Rate limit error creating dynamic lesson',
                `It looks like too many teachers are generating lessons right now! We appreciate your patience. Please wait a moment and try again.`,
              );
            }

            // Default error case
            return handleError(
              'AI.DynamicLesson.UnknownError',
              'Unknown error creating dynamic lesson',
              `Something didn't go as planned. Please try again, or reach out in the chat for help!`,
            );
          });
        return res.data;
      });
    },

    getTestcases: async ({
      presentationId,
    }: {
      presentationId: string;
    }): Promise<PromptTestcase[]> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<PromptTestcase[]>(
          `${this.url}/v0.5/presentations/${presentationId}/testcases`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    newTestcase: async ({
      inputData,
      presentationId,
    }: {
      inputData: PromptInputData[];
      presentationId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<PromptTestcase>(
          `${this.url}/v0.5/presentations/${presentationId}/testcases`,
          { inputData },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    runTestcases: async ({
      testcaseIds,
      presentationId,
      useSlidesAsSampleLesson = false,
    }: {
      testcaseIds: string[];
      presentationId: string;
      useSlidesAsSampleLesson?: boolean;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<
          ById<{
            slides: Slide[];
            allTestcasePromptOutputs?: PromptSingleTestCaseResult;
            lessonAttachments?: LessonAttachment[];
          }>
        >(
          `${this.url}/v0.5/generators/generate-by-testcase`,
          { testcaseIds, presentationId, useSlidesAsSampleLesson },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    duplicateTestcase: async ({
      presentationId,
      testcaseid,
    }: {
      testcaseid: string;
      presentationId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<PromptTestcase>(
          `${this.url}/v0.5/presentations/${presentationId}/testcases/${testcaseid}/duplicate`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    editTestcase: async ({
      inputData,
      presentationId,
      testcaseId,
      isSuggestion,
      isSampleLesson,
    }: {
      inputData: PromptInputData[];
      presentationId: string;
      testcaseId: string;
      isSuggestion?: boolean;
      isSampleLesson?: boolean;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<PromptTestcase>(
          `${this.url}/v0.5/presentations/${presentationId}/testcases/${testcaseId}`,
          { inputData, isSuggestion, isSampleLesson },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    deleteTestcase: async ({
      testcaseIds,
      presentationId,
    }: {
      testcaseIds: string[];
      presentationId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete<PromptTestcase>(
          `${this.url}/v0.5/presentations/${presentationId}/testcases`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
            data: { testcaseIds },
          },
        );
        return res.data;
      });
    },
    retrieveGenerator: async (generatorId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<DiscoveredTemplate>(
          `${this.url}/v0.5/generators/${generatorId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    createOrEditGenerator: async ({
      generatorId,
      status,
      isPremiumGenerator,
      generatorPlan,
      coverImageAltText,
      coverImageId,
      backgroundImageId,
      learningStandards,
      durationInMins,
    }: {
      generatorId: string;
      status: DiscoveredTemplateStatus;
      isPremiumGenerator?: boolean;
      generatorPlan?: GeneratorCoarseLicenseLevel | null;
      coverImageAltText?: string;
      coverImageId?: string;
      backgroundImageId?: string;
      learningStandards?: string[];
      durationInMins?: number;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<DiscoveredTemplate>(
          `${this.url}/v0.5/generators/${generatorId}`,
          {
            status,
            coverImageAltText,
            coverImageId,
            backgroundImageId,
            isPremiumGenerator,
            generatorPlan,
            learningStandards,
            durationInMins: isNaN(Number(durationInMins)) ? 0 : durationInMins,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    getStatus: async () => {
      const res = await axios.get<boolean>(`${this.url}/v0/heartbeat/ai-status`, {
        headers: getUnAuthHeader(getDestinationTenantFromUrl()),
      });
      return res.data;
    },
    getInsights: async ({
      livePresentationId,
      reportType,
    }: {
      livePresentationId: string;
      reportType: ReportInsightType;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<ReportInsight>(
          `${this.url}/v0.5/reports/${livePresentationId}/insight/${reportType}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    editReportInsight: async ({
      insightId,
      promptOutput,
    }: {
      insightId: string;
      promptOutput: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<ReportInsight | undefined>(
          `${this.url}/v0.5/reports/${insightId}`,
          { promptOutput },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    deleteReportInsight: async ({
      reportType,
      livePresentationId,
    }: {
      reportType: ReportInsightType;
      livePresentationId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete<ReportInsight | undefined>(
          `${this.url}/v0.5/reports/${livePresentationId}/reportType/${reportType}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    getParticipantInsights: async ({
      livePresentationId,
      reportType,
      participantId,
    }: {
      livePresentationId: string;
      reportType: ReportInsightType;
      participantId?: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<ReportInsight>(
          `${this.url}/v0.5/reports/${livePresentationId}/insight/${reportType}/participant/${participantId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    generateReportInsights: async ({
      livePresentationId,
      reportType,
      participantId,
    }: {
      livePresentationId: string;
      reportType: ReportInsightType | 'all';
      participantId?: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<ReportInsight | undefined>(
          `${this.url}/v0.5/reports/${livePresentationId}/generateInsights`,
          {
            reportType,
            participantId,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    generateReportInsightsForAllStudents: async ({
      livePresentationId,
    }: {
      livePresentationId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<ReportInsight[]>(
          `${this.url}/v0.5/reports/${livePresentationId}/generateInsights/allParticipants`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    generateTestResultInsights: async ({
      gameId,
      systemInstruction,
      userInstruction,
      insightId,
      temperature,
    }: {
      gameId: string;
      systemInstruction?: string;
      userInstruction?: string;
      insightId: string;
      temperature?: number;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<
          { insight: string; formattedTemplate: string } | undefined
        >(
          `${this.url}/v0.5/reports/${gameId}/generateInsights/promptTesting`,
          {
            userInstruction: userInstruction || null,
            systemInstruction: systemInstruction || null,
            insightId,
            temperature: temperature || null,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };
  invitations = {
    bulkInvite: async (invitations: EmailInvitationCreateDTO[]) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Invitation[]>(
          `${this.url}/v0.5/invitations/invite`,
          { invitations },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    generate: async (role: InvitationRole, topic?: unknown, duration?: number) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Invitation>(
          `${this.url}/v0.5/invitations/generate`,
          {
            role,
            topic,
            // Null should be a never expiring invitationLink
            expireHours: duration === -1 ? null : duration,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };

  generators = {
    listByIds: async (ids: string[]) => {
      if (ids.length === 0) return [] as ConsumerGenerator[];
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<ConsumerGenerator[]>(
          `${this.url}/v0.5/generators/byIds?ids=${ids.join(',')}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    retrieve: async (generatorId: string) => {
      const res = await axios.get<ConsumerGenerator>(
        `${this.url}/v0.5/generators/search/${generatorId}`,
        {
          headers: getUnAuthHeader(getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    },
    search: async ({
      cursor,
      grades,
      categories,
      limit,
      subjects,
      creatorId,
      searchPhrase,
      statuses,
    }: {
      cursor: number;
      subjects: string[];
      categories: string[];
      grades: string[];
      limit: number;
      creatorId?: string;
      searchPhrase?: string;
      statuses: string[];
    }) => {
      const toGeneratorSearchParams = ({
        cursor,
        grades,
        categories,
        limit,
        subjects,
        statuses,
        searchPhrase,
        creatorId,
      }: {
        cursor: number;
        subjects: string[];
        categories?: string[];
        creatorId?: string;
        searchPhrase?: string;
        grades: string[];
        statuses?: string[];
        limit: number;
      }) => {
        const searchParams = new URLSearchParams();
        if (limit) {
          searchParams.set('limit', limit.toString());
        }
        if (cursor) {
          searchParams.set('cursor', cursor.toString());
        }
        if (grades && grades.length > 0) {
          searchParams.set('grades', grades.join(','));
        }
        if (subjects && subjects.length > 0) {
          searchParams.set('subjects', subjects.sort().join(','));
        }

        if (categories && categories.length > 0) {
          searchParams.set('categories', categories.sort().join(','));
        }

        if (creatorId) {
          searchParams.set('creatorId', creatorId);
        }

        if (statuses && statuses.length > 0) {
          searchParams.set('statuses', statuses.sort().join(','));
        }
        if (searchPhrase && searchPhrase.length > 0) {
          searchParams.set('searchPhrase', searchPhrase);
        }

        return searchParams;
      };
      const queryString = toGeneratorSearchParams({
        cursor,
        subjects,
        categories: categories.filter(c => c !== 'none'), // hack to prevent searching with the "none" category
        grades,
        searchPhrase,
        creatorId,
        statuses,
        limit,
      });
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{
          items: ConsumerGenerator[];
          cursor: number;
          total: number;
        }>(`${this.url}/v0.5/generators/search/?${queryString.toString()}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },

    listSimilarGenerators: async ({ generatorId }: { generatorId: string }) => {
      const res = await axios.get<ConsumerGenerator[]>(
        `${this.url}/v0.5/generators/listSimilar?id=${generatorId}`,
        {},
      );
      return res.data;
    },
  };

  discover = {
    getGeneratorThumbnailUrl: (fileId: string) => `${this.cdnUrl}/${fileId}`,

    getTemplateThumbnail: async (_: string, fileId: string) => {
      const res = await axios.request<Blob>({
        method: 'get',
        url: `${this.cdnUrl}/${fileId}.jpeg`,
        responseType: 'blob',
      });
      const objectUrl = URL.createObjectURL(res.data);
      return objectUrl;
    },
  };
  workspace = {
    newWorkspace: (data: FolderEditOrCreateDTO) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Workspace | undefined>(
          `${this.url}/v0.5/workspace`,
          data,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    getWorkspace: async (workspaceId: string, activeTenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Folder>(`${this.url}/v0.5/workspace/${workspaceId}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
        });
        return res.data;
      });
    },
    listWorkspaces: async (activeTenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Workspace[]>(`${this.url}/v0.5/workspace`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
        });
        return res.data;
      });
    },
    saveWorkspace: (changes: { title: string }, workspaceId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<Workspace>(
          `${this.url}/v0.5/workspace/${workspaceId}`,
          { changes: changes },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    restoreWorkspaceTemplate: async (workspaceId: string, templateId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Template>(
          `${this.url}/v0.5/workspace/${workspaceId}/templates/${templateId}/restore`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    restoreWorkspaceFolder: async (workspaceId: string, folderId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Folder>(
          `${this.url}/v0.5/workspace/${workspaceId}/folders/${folderId}/restore`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    listWorkspaceTemplates: (
      workspaceId: string,
      parentId?: string,
      tenantId?: string,
    ): Promise<Template[]> => {
      let queryString = '';
      if (parentId) {
        queryString += '?parentId=' + parentId;
      }
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Template[]>(
          `${this.url}/v0.5/workspace/${workspaceId}/templates${queryString}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
          },
        );
        return res.data;
      });
    },
    listWorkspaceFolders: (workspaceId: string, tenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Folder[]>(
          `${this.url}/v0.5/workspace/${workspaceId}/folders`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
          },
        );
        return res.data;
      });
    },
    listDeletedWorkspaceTemplates: (workspaceId: string): Promise<Template[]> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Template[]>(
          `${this.url}/v0.5/workspace/${workspaceId}/templates/deleted`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    listDeletedWorkspaceFolders: (workspaceId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Folder[]>(
          `${this.url}/v0.5/workspace/${workspaceId}/folders/deleted`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };
  users = {
    hasReachedCreditsLimit: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{ hasReachedLimit: boolean }>(
          `${this.url}/v0.5/users/me/hasReachedLimit`,
          {
            headers: getAuthHeader(token),
          },
        );
        return res.data.hasReachedLimit;
      });
    },
    clearCache: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete<{ success: boolean }>(
          `${this.url}/v0.5/users/me/cache`,
          {
            headers: getAuthHeader(token),
          },
        );
        return res.data;
      });
    },
    deleteMe: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{ success: boolean }>(
          `${this.url}/v0/users/me/delete`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    getAccessBadge: async (activeTenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<RawUserAccessBadge>(
          `${this.url}/v0.5/users/me/accessBadge`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return new UserAccessBadge(
          res.data.role,
          res.data.user,
          res.data.license,
          res.data.organization,
          res.data.otherOrganizations,
          res.data.tenant,
          res.data.canPurchaseIndividualPremium,
        );
      });
    },

    updateSchoolInfo: async ({
      activeTenantId,
      schoolId,
      schoolCountry,
    }: {
      activeTenantId: string;
      schoolId?: number;
      schoolCountry?: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<RawUserAccessBadge>(
          `${this.url}/v0.5/users/me/accessBadge`,
          {
            schoolCountry: schoolCountry,
            schoolId: schoolId,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return new UserAccessBadge(
          res.data.role,
          res.data.user,
          res.data.license,
          res.data.organization,
          res.data.otherOrganizations,
          res.data.tenant,
          res.data.canPurchaseIndividualPremium,
        );
      });
    },

    setAssignmentConfirmation: async (
      confirmationState: ConfirmationState,
      durationDays?: number,
    ): Promise<void> => {
      return this.retryWithFreshTokenIf401(async token => {
        await axios.post(
          `${this.url}/v0.5/users/me/confirmAssignment`,
          {
            confirmationState,
            durationDays,
          },
          {
            headers: getAuthHeader(token),
          },
        );
      });
    },

    editProfile: async (params: {
      firstName?: string;
      lastName?: string;
      username?: string;
      schoolRole?: string;
      subjects?: string[];
      grades?: string[];
      signUpOrigin?: string;
      intendedUses?: string[];
      onboardingCompletedAt?: Date | null;
      preferredLocale?: string;
    }): Promise<User> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<User>(
          `${this.url}/v0.5/users/me/editProfile`,
          { ...params },
          {
            headers: getAuthHeader(token),
          },
        );
        return res.data;
      });
    },
    createIndividualCheckoutSession: async ({
      priceId,
      successUrl,
      cancelUrl,
    }: {
      priceId: string;
      successUrl?: string;
      cancelUrl?: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          sessionId: string;
          url: string;
        }>(
          `${this.url}/v0.5/payments/individual/create-checkout-session`,
          { priceId, successUrl, cancelUrl },
          {
            headers: getAuthHeader(token),
          },
        );
        return res.data;
      });
    },
    getCustomerPortalUrl: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          url: string;
        }>(
          `${this.url}/v0.5/payments/customer-portal`,
          {},
          {
            headers: getAuthHeader(token),
          },
        );
        return res.data;
      });
    },
  };

  public adjustAttachmentReadability = async ({
    attachmentId,
    presentationId,
    grade,
  }: {
    attachmentId: string;
    presentationId: string;
    grade: string;
  }) => {
    return this.retryWithFreshTokenIf401(async token => {
      const res = await axios.post<LessonAttachment>(
        `${this.url}/v0.5/presentations/attachments/${attachmentId}/adjustReadability`,
        {
          presentationId,
          grade,
        },
        {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    });
  };

  tenant = {
    getEmailDomains: async (activeTenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<string[]>(`${this.url}/v0.5/tenants/emailDomains`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
        });
        return res.data;
      });
    },
    editEmailDomains: async (emailDomains: string[], activeTenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<string[]>(
          `${this.url}/v0.5/tenants/emailDomains`,
          { emailDomains: emailDomains },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },
    editUserProfile: async ({
      role,
      userId,
    }: {
      role: TenantUserRole;
      userId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<TenantUser>(
          `${this.url}/v0.5/tenants/user/${userId}`,
          {
            role,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    removeUser: async (uid: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete(`${this.url}/v0.5/tenants/user/${uid}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
    listUsers: async (activeTenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{
          users: UserWithAccessRole[];
          byId: ById<UserWithAccessRole>;
        }>(`${this.url}/v0.5/tenants/users`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
        });
        return res.data;
      });
    },
    getUser: async (uid: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<User>(`${this.url}/v0.5/tenants/user/${uid}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
    copyLessonToTenant: async ({
      toTenantId,
      fromTenantId,
      lessonId,
    }: {
      toTenantId: string;
      fromTenantId: string;
      lessonId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<boolean>(
          `${this.url}/v0.5/tenants/copyLessonBetweenTenants`,
          {
            toTenantId,
            fromTenantId,
            lessonId,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    copyFolderToTenant: async ({
      toTenantId,
      fromTenantId,
      folderId,
    }: {
      toTenantId: string;
      fromTenantId: string;
      folderId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<boolean>(
          `${this.url}/v0.5/tenants/copyFolderBetweenTenant`,
          {
            toTenantId,
            fromTenantId,
            folderId,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };

  gif = {
    search: async ({
      search,
      limit = 20,
      cursor,
    }: {
      search?: string;
      limit?: number;
      cursor?: string;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.set('limit', limit?.toString());
      if (cursor) {
        queryParams.set('cursor', cursor);
      }

      if (search) {
        queryParams.set('query', search);
      }

      const query = queryParams.toString();

      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{
          cursor?: string;
          data: TenorGIF[];
        }>(`${this.url}/v0.5/gif/search?${query}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
  };

  blueprint = {
    saveFolder: (changes: FolderEditOrCreateDTO) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<Folder>(
          `${this.url}/v0.5/folders/${changes.id}`,
          { changes: changes },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    duplicateTemplate: async (templateId: string, parentId: string | null) => {
      const tenantId = getDestinationTenantFromUrl();
      return this.retryWithFreshTokenIf401(async token => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = await axios.post<{ template: Template; errors: any[] }>(
          `${this.url}/v0.5/templates/copyTemplate`,
          { templateId, tenantId, parentId },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    retrieveTemplate: (templateId: string): Promise<Template | undefined> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Template | undefined>(
          `${this.url}/v0.5/templates/${templateId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    retrieveTemplateAuthenticated: (
      templateId: string,
      tenantId: string,
    ): Promise<Template | undefined> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Template | undefined>(
          `${this.url}/v0.5/templates/private/${templateId}/tenantId/${tenantId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    retriveTemplateUnauthenticated: async (
      tenantId: string,
      templateId: string,
    ): Promise<Template | undefined> => {
      const res = await axios.get<Template | undefined>(
        `${this.url}/v0.5/templates/public/${templateId}/tenantId/${tenantId}`,
        {
          headers: getUnAuthHeader(getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    },
  };
  gameFiles = {
    importPresentation: async (
      templateId: string,
      fileId: string,
      fileExtension: string,
    ) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Slide[]>(
          `${this.url}/v0.5/templates/${templateId}/importPresentation`,
          {
            presentationFilePath: fileId,
            presentationFileExtension: fileExtension,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    fetchAttachmentContent: async ({
      fileId,
      isFromDiscover,
    }: {
      fileId: string;
      isFromDiscover: boolean;
    }): Promise<CuripodElement[]> => {
      const url = `${this.url}/v0.5/${
        isFromDiscover ? 'discover' : 'game_images'
      }/public/filesV2/${fileId}`;
      const res = await axios.request<string>({
        method: 'get',
        url,
        headers: {
          ...getUnAuthHeader(getDestinationTenantFromUrl()),
          'Cache-Control': 'no-cache',
        },
      });

      const signedUrl = res.data;

      const rawFileContent = await axios.request<{
        version: number;
        type: 'passage';
        content: CuripodElement[];
      }>({
        method: 'get',
        url: signedUrl,
        responseType: 'json',
      });

      return rawFileContent.data.content;
    },

    fetchCroppedImageV2: async ({
      fileId,
      isAuthenticated,
      isFromDiscover,
      cropConfig,
      isOwner,
    }: {
      fileId: string;
      cropConfig?: CropConfig | null;
      isAuthenticated?: boolean;
      isFromDiscover: boolean;
      isOwner: boolean;
    }) =>
      await retry(
        async (retrier, attemptNumber) => {
          const query = cropConfig
            ? `?h=${cropConfig.height}&w=${cropConfig.width}&x=${cropConfig.x}&y=${cropConfig.y}`
            : undefined;
          if (isFromDiscover) {
            const url = `${this.url}/v0.5/discover/public/filesV2/${fileId}${
              query ? query : ''
            }`;

            const res = await axios.request<string>({
              method: 'get',
              url,
              headers: {
                ...getUnAuthHeader(getDestinationTenantFromUrl()),
                ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
              },
            });

            const signedUrl = res.data;

            //we want to prefetch the image so also get the image blob from cdn
            const cdnImageResponse = await axios.request<Blob>({
              method: 'get',
              url: signedUrl,
              responseType: 'blob',
            });
            const objectUrl = URL.createObjectURL(cdnImageResponse.data);
            return objectUrl;
          }
          if (isAuthenticated === true && isOwner) {
            return this.retryWithFreshTokenIf401(async token => {
              const url = `${this.url}/v0.5/game_images/filesV2/${fileId}${
                query ? query : ''
              }`;
              const res = await axios.request<string>({
                method: 'get',
                url,
                headers: {
                  ...getAuthHeader(token, getDestinationTenantFromUrl()),
                  ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
                },
              });

              const signedUrl = res.data;

              //we want to prefetch the image so also get the image blob from cdn
              const cdnImageResponse = await axios.request<Blob>({
                method: 'get',
                url: signedUrl,
                responseType: 'blob',
              });
              const objectUrl = URL.createObjectURL(cdnImageResponse.data);
              return objectUrl;
            });
          } else {
            const url = `${this.url}/v0.5/game_images/public/filesV2/${fileId}${
              query ? query : ''
            }`;
            const res = await axios.request<string>({
              method: 'get',
              url,
              headers: {
                ...getUnAuthHeader(getDestinationTenantFromUrl()),
                ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
              },
            });

            const signedUrl = res.data;

            //we want to prefetch the image so also get the image blob from cdn
            const cdnImageResponse = await axios.request<Blob>({
              method: 'get',
              url: signedUrl,
              responseType: 'blob',
            });
            const objectUrl = URL.createObjectURL(cdnImageResponse.data);
            return objectUrl;
          }
        },
        {
          retries: 3,
        },
      ),

    fetchSvgImage: async ({
      fileId,
      isAuthenticated,
      isFromDiscover,
      cropConfig,
      isOwner,
    }: {
      fileId: string;
      cropConfig?: CropConfig | null;
      isAuthenticated?: boolean;
      isFromDiscover: boolean;
      isOwner: boolean;
    }) =>
      await retry(
        async (retrier, attemptNumber) => {
          const query = cropConfig
            ? `?h=${cropConfig.height}&w=${cropConfig.width}&x=${cropConfig.x}&y=${cropConfig.y}`
            : undefined;
          if (isFromDiscover) {
            const url = `${this.url}/v0.5/discover/public/filesV2/${fileId}${
              query ? query : ''
            }`;

            const res = await axios.request<string>({
              method: 'get',
              url,
              headers: {
                ...getUnAuthHeader(getDestinationTenantFromUrl()),
                ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
              },
            });

            const signedUrl = res.data;

            //we want to prefetch the image so also get from cdn
            const cdnImageResponse = await axios.request({
              method: 'get',
              url: signedUrl,
            });

            return cdnImageResponse.data;
          }
          // This image is not from discover - we use game_images endpoints
          if (isAuthenticated === true && isOwner) {
            return this.retryWithFreshTokenIf401(async token => {
              const url = `${this.url}/v0.5/game_images/filesV2/${fileId}${
                query ? query : ''
              }`;
              const res = await axios.request<string>({
                method: 'get',
                url,
                headers: {
                  ...getAuthHeader(token, getDestinationTenantFromUrl()),
                  ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
                },
              });

              const signedUrl = res.data;

              //we want to prefetch the image so also get the image blob from cdn
              const cdnImageResponse = await axios.request({
                method: 'get',
                url: signedUrl,
              });

              return cdnImageResponse.data;
            });
          } else {
            const url = `${this.url}/v0.5/game_images/public/filesV2/${fileId}${
              query ? query : ''
            }`;

            const res = await axios.request<string>({
              method: 'get',
              url,
              headers: {
                ...getUnAuthHeader(getDestinationTenantFromUrl()),
                ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
              },
            });

            const signedUrl = res.data;

            //we want to prefetch the image so also get the image blob from cdn
            const cdnImageResponse = await axios.request({
              method: 'get',
              url: signedUrl,
            });

            return cdnImageResponse.data;
          }
        },
        {
          retries: 3,
        },
      ),

    getGifImage: async (fileId: string) => {
      const downloadRes = await this.getDownloadUrl(fileId);
      const url = downloadRes.data.url;
      const res = await Axios.request<Blob>({
        method: 'get',
        url,
        responseType: 'blob',
      });
      const objUrl = URL.createObjectURL(res.data);
      return objUrl;
    },
  };

  paywall = {
    retrieveCredits: async (activeTenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{ translationCredits: number }>(
          `${this.url}/v0.5/credits`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },
  };

  template = {
    translateSlides: async ({
      originLanguage,
      outputLanguage,
      slides,
      lessonAttachments,
      activeTenantId,
    }: {
      slides: Slide[];
      lessonAttachments: LessonAttachment[];
      originLanguage: string;
      outputLanguage: string;
      activeTenantId?: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          slides: Slide[];
          lessonAttachments: LessonAttachment[];
        }>(
          `${this.url}/v0.5/templates/translateSlidesAndAttachments`,
          { slides, lessonAttachments, originLanguage, outputLanguage },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
            timeout: 1000 * slides.length * 75,
          },
        );
        return res.data;
      });
    },

    duplicateTemplate: async ({
      parentId,
      templateId,
      activeTenantId,
      allowGeneratorPresentation,
      isPrivate,
    }: {
      templateId: string;
      parentId: string | null;
      isPrivate?: boolean;
      activeTenantId?: string;
      allowGeneratorPresentation?: boolean;
    }) => {
      const tenantId = getDestinationTenantFromUrl();
      return this.retryWithFreshTokenIf401(async token => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = await axios.post<{ template: Template; errors: any[] }>(
          `${this.url}/v0.5/templates/copyTemplate`,
          { templateId, tenantId, parentId, isPrivate, allowGeneratorPresentation },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },

    retrieveTemplate: (templateId: string): Promise<Template | undefined> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Template | undefined>(
          `${this.url}/v0.5/templates/${templateId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    listTemplates: (parentId?: string | null, tenantId?: string): Promise<Template[]> => {
      let queryString = '';
      if (parentId) {
        queryString += '?parentId=' + parentId;
      }
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Template[]>(
          `${this.url}/v0.5/templates${queryString}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
          },
        );
        return res.data;
      });
    },

    listRecentTemplates: ({
      limit,
      cursor,
      tenantId,
      isPrivate,
    }: {
      limit: number;
      tenantId?: string;
      cursor?: number;
      isPrivate?: boolean;
    }): Promise<{ items: Template[]; cursor: string }> => {
      return this.retryWithFreshTokenIf401(async token => {
        const searchParams = new URLSearchParams();
        searchParams.set('limit', limit.toString());
        if (isPrivate !== undefined) {
          searchParams.set('isPrivate', isPrivate.toString());
        }
        const queryString = `${searchParams.toString()}`;

        let url = `${this.url}/v0.5/templates/recent?${queryString}`;
        if (cursor) url = url + `&cursor=${cursor}`;
        const res = await axios.get<{ items: Template[]; cursor: string }>(url, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
        });
        return res.data;
      });
    },
    listDeletedTemplates: (): Promise<Template[]> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Template[]>(`${this.url}/v0.5/templates/deleted`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
    listFolders: (tenantId?: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Folder[]>(`${this.url}/v0.5/folders`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
        });
        return res.data;
      });
    },
    listDeletedFolders: () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Folder[]>(`${this.url}/v0.5/folders/deleted`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },

    newFolder: (data: FolderEditOrCreateDTO) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Folder>(`${this.url}/v0.5/folders`, data, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
    saveFolder: (changes: FolderEditOrCreateDTO) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<Folder>(
          `${this.url}/v0.5/folders/${changes.id}`,
          { changes: changes },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    restoreFolder: (folderId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<Folder>(
          `${this.url}/v0.5/folders/${folderId}/restore`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    templateAutoSave: async (
      data?: TemplateEditOrCreateDTO | null,
      activeTenantId?: string,
    ) => {
      // Make sure we pass in undefined if the data.id = "new";
      const id = data?.id === NEW_TEMPLATE_PARAM ? undefined : data?.id;

      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<Template>(
          `${this.url}/v0.5/templates`,
          {
            ...data,
            id,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },
    restoreTemplate: async (templateId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<Template>(
          `${this.url}/v0.5/templates/${templateId}/restore`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    deleteTemplate: (templateId: string): Promise<string> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete(`${this.url}/v0.5/templates/${templateId}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
    deleteFolder: (folderId: string): Promise<string[]> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete(`${this.url}/v0.5/folders/${folderId}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
    createGameFromTemplate: ({
      templateId,
      isFromDiscover,
      title,
      activeTenantId,
      slides,
      requireRealNames,
      isContentFilterEnabled,
      currentRound,
      metadata,
      attachments,
    }: {
      templateId?: string;
      title?: string | null;
      requireRealNames: boolean;
      isContentFilterEnabled: boolean;
      isFromDiscover?: boolean;
      activeTenantId?: string;
      slides: Slide[];
      currentRound?: string | null;
      metadata?: ById<string | boolean | number> | null;
      attachments?: LessonAttachment[];
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Game>(
          `${this.url}/v0.5/templates/createLivePresentation`,
          {
            gameLanguage: 'en',
            templateId,
            isFromDiscover: isFromDiscover,
            slides,
            title: title,
            requireRealNames: requireRealNames,
            isContentFilterEnabled,
            currentRound,
            metadata,
            attachments,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },
  };
  game = {
    kickParticipant: async ({
      gameId,
      participantId,
    }: {
      gameId: string;
      participantId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post(
          `${this.url}/v0.5/games/${gameId}/kickParticipant`,
          { participantId },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    retrievePoll: async ({ gameId, roundId }: { gameId: string; roundId: string }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<RoundOptions[]>(
          `${this.url}/v0.5/games/${gameId}/slides/${roundId}/poll`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    retrievePublicPoll: async ({
      gameId,
      roundId,
    }: {
      gameId: string;
      roundId: string;
    }) => {
      const res = await axios.get<RoundOptions[]>(
        `${this.url}/v0.5/games/public/${gameId}/slides/${roundId}/poll`,
        {
          headers: getUnAuthHeader(getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    },
    retrievePublicResponses: async ({
      gameId,
      roundId,
    }: {
      gameId: string;
      roundId: string;
    }) => {
      const res = await axios.get<Response[]>(
        `${this.url}/v0.5/games/public/${gameId}/slides/${roundId}/responses`,
        {
          headers: getUnAuthHeader(getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    },
    retrievePublicAiFeedback: async ({
      gameId,
      slideId,
    }: {
      gameId: string;
      slideId: string;
    }) => {
      const res = await axios.get<{ results: AiAssessedScore[] }>(
        `${this.url}/v0.5/games/public/${gameId}/slides/${slideId}/listAiAssessments`,
        {
          headers: getUnAuthHeader(getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    },
    retrieveAllAIFeedbackForGame: async ({ gameId }: { gameId: string }) => {
      const res = await axios.get<{
        items: { aiAssessedScores: AiAssessedScore[]; slideId: string }[];
      }>(`${this.url}/v0.5/games/public/${gameId}/listAllAIFeedback`, {
        headers: getUnAuthHeader(getDestinationTenantFromUrl()),
      });
      return res.data;
    },
    retrieveWordcloudResults: async ({
      gameId,
      slideId,
    }: {
      gameId: string;
      slideId: string;
    }) => {
      /**@deprecated */
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Score[]>(
          `${this.url}/v0.5/games/${gameId}/slides/${slideId}/wordcloudresults`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );

        return res.data;
      });
    },
    retrieveWordcloud: async ({
      gameId,
      slideId,
    }: {
      gameId: string;
      slideId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Score[]>(
          `${this.url}/v0.5/games/${gameId}/slides/${slideId}/wordcloud`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );

        return res.data;
      });
    },
    retrievePublicWordcloud: async ({
      gameId,
      slideId,
    }: {
      gameId: string;
      slideId: string;
    }) => {
      const res = await axios.get<Score[]>(
        `${this.url}/v0.5/games/public/${gameId}/slides/${slideId}/wordcloud`,
        {
          headers: getUnAuthHeader(getDestinationTenantFromUrl()),
        },
      );

      return res.data;
    },

    retrieveResults: async (gameId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<ActivityResults>(
          `${this.url}/v0.5/games/${gameId}/results`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    autoSave: async (data: GameEditOrCreateDTO) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Game>(
          `${this.url}/v0.5/games/autoSave`,
          {
            ...data,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    addNewActivity: async (data: {
      gameId: string;
      type: RoundType;
      title?: string | null;
      votingTitle?: string | null;
      hasVoting: boolean;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        await axios.post(
          `${this.url}/v0.5/games/${data.gameId}/addActivity`,
          {
            ...data,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
      });
    },

    dismissQuestion: ({
      responseId,
      roundId,
      gameId,
      dismiss,
    }: {
      responseId: string;
      roundId: string;
      gameId: string;
      dismiss: boolean;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<void>(
          `${this.url}/v0.5/games/${gameId}/slides/${roundId}/responses/${responseId}/dismiss`,
          { dismiss },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    generateNewPin: (gameId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Game>(
          `${this.url}/v0.5/games/${gameId}/init`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    exportExcelResults: (gameId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{ url: string }>(
          `${this.url}/v0.5/games/${gameId}/export`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },

    createPreviewGame: (
      templateId: string,
      isDemoGame: boolean,
      isFromDiscover: boolean,
      activeTenantId?: string,
    ) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Game>(
          `${this.url}/v0.5/templates/${templateId}/preview${
            isDemoGame ? '?isDemoGame=true' : ''
          }`,
          { isFromDiscover: isFromDiscover },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },
    deleteGame: (gameId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete(`${this.url}/v0.5/games/${gameId}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },

    listResults: (limit: number, cursor?: number, templateId?: string | null) => {
      return this.retryWithFreshTokenIf401(async token => {
        let url = `${this.url}/v0.5/games/paginatedResults?${
          templateId ? `templateId=${templateId}` : ''
        }&limit=${limit}`;
        if (cursor) url = url + `&cursor=${cursor}`;
        const res = await axios.get<PaginatedGameResults>(url, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });

        return res.data;
      });
    },
    listResponsesForRound: ({ gameId, roundId }: { gameId: string; roundId: string }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Response[]>(
          `${this.url}/v0.5/games/${gameId}/rounds/${roundId}/listResponses`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    retrieveLeaderBoard: (slideId: string, gameId: string, includeAll?: boolean) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<LeaderBoard>(
          `${this.url}/v0.5/games/${gameId}/leaderboard/${slideId}${
            includeAll ? '?includeAll=true' : ''
          }`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    retrievePublicLeaderBoard: async (
      slideId: string,
      gameId: string,
      includeAll?: boolean,
    ) => {
      const res = await axios.get<LeaderBoard>(
        `${this.url}/v0.5/games/public/${gameId}/leaderboard/${slideId}${
          includeAll ? '?includeAll=true' : ''
        }`,
        {
          headers: getUnAuthHeader(getDestinationTenantFromUrl()),
        },
      );
      return res.data;
    },
    retrieve: (gameId?: string | undefined) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Game>(`${this.url}/v0.5/games/${gameId}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },

    end: (gameId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<void>(
          `${this.url}/v0.5/games/${gameId}/endGame`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
            timeout: 30 * 1000,
          },
        );
        return res.data;
      });
    },
    setAllRoundsAsCompleted: (gameId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Game>(
          `${this.url}/v0.5/games/${gameId}/setAllRoundsAsCompleted`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    editTitle: ({ gameId, title }: { gameId: string; title: string }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<Game>(
          `${this.url}/v0.5/games/${gameId}/editTitle`,
          {
            title,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };

  feedback = {
    submit: async (message: string, activeTenantId: string, accepted?: boolean) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post(
          `${this.url}/v0/feedback`,
          {
            message,
            accepted,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)), //This might not be needed for a feedback
          },
        );
        return res.data;
      });
    },
  };
  testRequest = {
    submit: async (state: string, nameOfTest: string, activeTenantId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post(
          `${this.url}/v0/feedback/testRequest`,
          {
            state,
            nameOfTest,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },
  };

  standards = {
    fetchAllStandards: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<Standards[]>(`${this.url}/v0.5/standards`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()),
        });
        return res.data;
      });
    },
    createStandard: async ({
      label,
      description,
    }: {
      label: string;
      description: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<unknown>(
          `${this.url}/v0.5/standards/create`,
          { label, description },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };

  districtData = {
    getDistrictData: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<DistrictUsageData[]>(
          `${this.url}/v0.5/districtUsage/data`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    getSchoolData: async ({ orgId }: { orgId?: string }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<SchoolUsageData[]>(
          `${this.url}/v0.5/districtUsage/schoolData?orgId=${orgId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };

  moderation = {
    join: async (pin: string, activeTenantId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{ id: string; tenantId: string }>(
          `${this.url}/v0.5/moderation/join`,
          { pin },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },
  };

  export = {
    createToken: async ({
      activeTenantId,
      email,
    }: {
      activeTenantId?: string;
      email?: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{ token: string }>(
          `${this.url}/v0.5/export/createToken`,
          {
            email,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)), //This might not be needed for a feedback
          },
        );
        return res.data;
      });
    },
    checkToken: async ({
      activeTenantId,
      token: tokenToVerify,
    }: {
      activeTenantId?: string;
      token: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          verified: true;
          payload: ExportTokenPayload;
          tenantName: string;
        }>(
          `${this.url}/v0.5/export/checkToken`,
          {
            token: tokenToVerify,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)), //This might not be needed for a feedback
          },
        );
        return res.data;
      });
    },
    execute: async ({
      activeTenantId,
      token: tokenToVerify,
    }: {
      activeTenantId?: string;
      token: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post(
          `${this.url}/v0.5/export/execute`,
          {
            token: tokenToVerify,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)), //This might not be needed for a feedback
          },
        );
        return res.data;
      });
    },
  };

  presentations = {
    generateActivities: async ({
      activities,
    }: {
      activities: ActivityGenerationVariables[];
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          id: string;
        }>(
          `${this.url}/v0.5/activities/generate`,
          { activities },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    newPresentationId: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          id: string;
        }>(
          `${this.url}/v0.5/presentations/new`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    search: async (query: PresentationQuery) => {
      const searchParams = new URLSearchParams();

      searchParams.set('search', query.search);
      searchParams.set('isPrivate', query.isPrivate.toString());

      if (query.parentFolderId) {
        searchParams.set('parentFolderId', query.parentFolderId);
      }
      if (query.limit) {
        searchParams.set('limit', query.limit.toString());
      }
      if (query.cursor) {
        searchParams.set('cursor', query.cursor.toString());
      }

      const queryString = query ? `${searchParams.toString()}` : '';
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{
          items: PresentationDTO[];
          cursor: number;
          total: number;
        }>(`${this.url}/v0.5/presentations/search?${queryString}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl()), //This might not be needed for a feedback
        });
        return res.data;
      });
    },
    list: async (query: {
      parentId?: string;
      isPrivate: boolean;
      limit?: number;
      cursor?: string;
      orderBy?: 'modifiedAt' | 'title';
      sortDirection?: 'asc' | 'desc';
    }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('isPrivate', query.isPrivate.toString());

      if (query.parentId) {
        searchParams.set('parentId', query.parentId);
      }
      if (query.limit) {
        searchParams.set('limit', query.limit.toString());
      }
      if (query.cursor) {
        searchParams.set('cursor', query.cursor.toString());
      }
      if (query.orderBy) {
        searchParams.set('orderBy', query.orderBy.toString());
      }
      if (query.sortDirection) {
        searchParams.set('sortDirection', query.sortDirection.toString());
      }

      const queryString = query ? `?${searchParams.toString()}` : '';
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{ items: PresentationDTO[]; cursor: string }>(
          `${this.url}/v0.5/presentations/${queryString}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()), //This might not be needed for a feedback
          },
        );
        return res.data;
      });
    },

    createLessonFromSample: async ({
      presentationId,
      slides,
      tenantId,
      isPresented,
      lessonAttachments,
    }: {
      presentationId: string;
      slides: Slide[];
      tenantId: string;
      isPresented?: boolean;
      lessonAttachments?: LessonAttachment[];
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          presentation: PresentationDTO;
          livePresentationId?: string;
        }>(
          `${this.url}/v0.5/presentations/create-lesson-from-sample`,
          { presentationId, slides, isPresented, lessonAttachments },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(tenantId)),
          },
        );
        return res.data;
      });
    },
    createAttachment: async ({ title, content }: { title: string; content: string }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<LessonAttachment>(
          `${this.url}/v0.5/presentations/attachments`,
          { title, content },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    createAttachmentContentFromFile: async ({
      fileId,
      fileType,
    }: {
      fileId: string;
      fileType: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<CuripodPassageFile & { title: string }>(
          `${this.url}/v0.5/presentations/attachments/fromFile`,
          { fileId, fileType },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    editAttachment: async ({
      title,
      content,
      attachmentId,
    }: {
      title: string;
      content: string;
      attachmentId: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.put<LessonAttachment>(
          `${this.url}/v0.5/presentations/attachments/${attachmentId}`,
          { title, content },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };
  integrations = {
    getMetadata: async (
      integrationLabel: SupportedIntegrationLabel,
      activeTenantId: string,
    ): Promise<SupportedIntegrationResponse> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get(`${this.url}/v0.5/integrations/${integrationLabel}`, {
          headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
        });
        return res.data;
      });
    },
    refreshMetadata: async (
      integrationLabel: SupportedIntegrationLabel,
      activeTenantId: string,
    ): Promise<SupportedIntegrationResponse> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post(
          `${this.url}/v0.5/integrations/${integrationLabel}`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
          },
        );
        return res.data;
      });
    },

    googleDrive: {
      copyFileToBucket: async (
        fileId: string,
        activeTenantId: string,
        preferredMimeType?: PreferredMimeType,
      ): Promise<{ filePath: string; fileType: string }> => {
        return this.retryWithFreshTokenIf401(async token => {
          const res = await axios.post(
            `${this.url}/v0.5/integrations/${SupportedIntegrationLabel.GoogleDrive}/copyFileToBucket`,
            { fileId: fileId, preferredMimeType },
            {
              headers: getAuthHeader(token, getDestinationTenantFromUrl(activeTenantId)),
            },
          );
          return res.data;
        });
      },
    },
  };
  organizations = {
    autocomplete: async ({
      query,
      type,
      zipCode,
      country,
      region,
      city,
      limit,
      similarityThreshold,
    }: {
      query: string;
      type: OrganizationType;
      zipCode: string;
      country: string;
      region: string;
      city: string;
      limit?: number;
      similarityThreshold?: number;
    }): Promise<OrganizationAutocomplete[]> => {
      const queryString = new URLSearchParams({
        query,
        type,
        zipCode,
        country,
        region,
        city,
        ...(notUndefined(limit) ? { limit: limit.toString() } : {}),
        ...(notUndefined(similarityThreshold)
          ? { similarityThreshold: similarityThreshold.toString() }
          : {}),
      });

      const res = await axios.get(
        `${this.url}/v0.5/organizations/autocomplete?${queryString}`,
      );
      return (res.data.items || []) as OrganizationAutocomplete[];
    },
    reselectOrganization: async (params: {
      newOrgId?: string;
      customOrgName?: string;
      city?: string;
      region?: string;
      country?: string;
      primaryZipCode?: string;
      organizationType?: OrganizationType;
    }): Promise<Tenant> => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post(
          `${this.url}/v0.5/organizations/reselection`,
          { ...params },
          {
            headers: getAuthHeader(token),
          },
        );
        return res.data.selectedTenant;
      });
    },
  };

  events = {
    trackEvent: async (
      eventName: string,
      payload?: { [key: string]: unknown },
    ): Promise<void> => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          'x-app-version': `v${pkg.version}`,
        };

        const eventPayload = {
          eventName,
          timestamp: new Date().toISOString(),
          ...payload,
        };

        await axios.post(`${this.url}/v0.5/events`, eventPayload, { headers });
      } catch (error) {
        newRelicNoticeError(error, 'Failed to track event');
        throw error;
      }
    },
  };

  agent = {
    retrieveHistory: async (presentationId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{ messages: LLMMessageDTO[] }>(
          `${this.url}/v0.5/agent/${presentationId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    retrieveLearningObjectiveHistory: async (threadId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.get<{ messages: LLMMessageDTO[] }>(
          `${this.url}/v0.5/agent/${threadId}/byThreadId`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    clearHistory: async (presentationId: string) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.delete<{ success: boolean }>(
          `${this.url}/v0.5/agent/${presentationId}`,
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };

  referrals = {
    sendReferrals: async ({
      emails,
      subject,
      message,
      senderFirstName,
      senderLastName,
    }: {
      emails: string[];
      subject: string;
      message: string;
      senderFirstName: string;
      senderLastName: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          success: boolean;
          emailsSent: number;
          emailsSkipped: number;
          totalRequested: number;
        }>(
          `${this.url}/v0.5/users/me/sendReferrals`,
          {
            emails,
            subject,
            message,
            senderFirstName,
            senderLastName,
          },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    grantReferralTrial: async () => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          success: boolean;
          trialGranted: boolean;
          trialEndDate?: Date;
        }>(
          `${this.url}/v0.5/users/me/grantReferralTrial`,
          {},
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
    validateEmails: async ({ emails }: { emails: string[] }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<EmailValidationResponse>(
          `${this.url}/v0.5/users/me/validateEmail`,
          { emails },
          {
            headers: getAuthHeader(token, getDestinationTenantFromUrl()),
          },
        );
        return res.data;
      });
    },
  };

  marketing = {
    trackConversion: async ({
      fbc,
      ttclid,
      userAgent,
      eventType,
      platform,
    }: {
      fbc?: string;
      ttclid?: string;
      userAgent: string;
      eventType: string;
      platform?: 'facebook' | 'tiktok';
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const payload: ConversionTrackingPayload = {
          userAgent,
          eventType,
        };

        if (fbc) payload.fbc = fbc;
        if (ttclid) payload.ttclid = ttclid;
        if (platform) payload.platform = platform;

        const res = await axios.post<{
          success: boolean;
          message: string;
          platform: string;
          apiResponse?: unknown;
        }>(`${this.url}/v0.5/marketing/conversion`, payload, {
          headers: getAuthHeader(token),
        });
        return res.data;
      });
    },
    trackActivation: async ({
      userAgent,
      eventType,
    }: {
      userAgent: string;
      eventType: string;
    }) => {
      return this.retryWithFreshTokenIf401(async token => {
        const res = await axios.post<{
          success: boolean;
          message: string;
          platform?: string;
          apiResponse?: unknown;
        }>(
          `${this.url}/v0.5/marketing/activation`,
          {
            userAgent,
            eventType,
          },
          {
            headers: getAuthHeader(token),
          },
        );
        return res.data;
      });
    },
  };

  geoip = {
    getLocation: async () => {
      const res = await axios.get<GeoLocationData>(`${this.url}/v0.5/geoip`, {
        headers: getUnAuthHeader(getDestinationTenantFromUrl()),
      });
      return res.data;
    },
  };
}

export type LLMToolCall = {
  id: string;
  callId: string;
  messageId: string;
  name: string;
  arguments: object;
  generatedFiles?: FileUploads[];
  content: string | null;
  createdAt: string;
  modifiedAt: string;
};

export const LLMMessageRole = {
  USER: 'USER',
  ASSISTANT: 'ASSISTANT',
  SYSTEM: 'SYSTEM',
} as const;

export type LLMMessageRole = typeof LLMMessageRole[keyof typeof LLMMessageRole];

type LLMMessage = {
  id: string;
  model: string;
  role: LLMMessageRole;
  content: string | null;
  threadId: string;
  createdAt: string;
  modifiedAt: string;
};

export type GenerationCheckpoint = {
  id: string;
  presentationVersionId?: string; // TODO remove Fallback to the old presentationVersionId for backward compatibility after presentationVersion migration
  presentationVersionIndexedId?: string;
  llmMessageId: string;
  createdAt: string;
  modifiedAt: string;
};

export type LLMMessageDTO = LLMMessage & {
  toolCalls?: LLMToolCall[];
  generationCheckpoint?: GenerationCheckpoint;
  attachments?: FileUploads[];
};

export type OrganizationAutocomplete = {
  id: string;
  name: string;
  city: string;
  region: string;
  primaryZipCode: string;
};

export type ExportTokenPayload = {
  tenantId: string;
  uid: string;
  issuedBy?: string;
  // token fields
  iat: number;
  exp: number;
  aud: string;
  iss: string;
};

export type LookupTenant = { tenantId: string; tenantName: string };

export type ConversionTrackingPayload = {
  userAgent: string;
  eventType: string;
  fbc?: string;
  ttclid?: string;
  platform?: 'facebook' | 'tiktok';
};

export type PresentationQuery = {
  search: string;

  isPrivate: boolean; // Optional to support workspace search
  parentFolderId?: string; // Optional to support folder search

  cursor?: number;
  limit: number;
};

export type LessonAttachment = {
  attachmentId: string;
  originAttachmentId?: string;
  type: 'passage';
  title: string;
  fileId: string;
  fileMimetype: 'application/json';
  modifiedAt: number;
};

export type CuripodPassageFile = {
  type: 'passage';
  version: number;
  content: CuripodDecendant[];
};

export type User = {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email: string;
  description?: string;
  profileImageId?: string;
  activeTenantUserId: string;
  createdAt: Date;
  modifiedAt: Date;
  deletedAt?: Date;
  profileInfo?: Record<string, unknown>;
  onboardingCompletedAt?: number;
  preferredLocale?: string;
};

export type TenantUser = {
  id: string;
  role: TenantUserRole;
  tenantId: string;
  userId: string;
  createdAt: Date;
  modifiedAt: Date;
};

export type TenantUserRole = 'Admin' | 'Member';

export type UserWithAccessRole = User & { role: TenantUserRole };

export type Tenant = {
  id: string;
  name: string;
  createdAt: Date;
  modifiedAt: Date;
};

export type EmailValidationResult = {
  email: string;
  userExists: boolean;
  validation: {
    status: string;
    reason: string;
    score: number;
    domain: {
      name: string;
      acceptAll: string;
      disposable: string;
      free: string;
    };
    account: {
      role: string;
      disabled: string;
      fullMailbox: string;
    };
    isDeliverable: boolean;
    isRisky: boolean;
  } | null;
  error: string | null;
};

export type EmailValidationResponse = {
  results: EmailValidationResult[];
  totalEmails: number;
};

export type GeoLocationData = {
  ip: string;
  country: string | null;
  countryName?: string;
  isLocal?: boolean;
  error?: string;
};

export type ActivityGenerationVariables = {
  type: string;
  grade: string;
  subject: string;
  language: string;
  formVariables: Record<string, string>;
  attachments: { fileUploadId: string }[];
  standard?: string;
};
